import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Project, Sub, WarrantyClaim } from '../types'
import { daysBetween, fmtDate } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

const STATUS_COLOR: Record<WarrantyClaim['status'], BadgeColor> = {
  submitted: 'red',
  acknowledged: 'gold',
  dispatched: 'gold',
  in_progress: 'gold',
  resolved: 'green',
  closed: 'gray',
  rejected: 'gray',
}

const CATEGORIES: WarrantyClaim['category'][] = [
  'workmanship',
  'systems',
  'structural',
  'maintenance',
  'other',
]

export default function WarrantyTab({ project }: { project: Project }) {
  const [claims, setClaims] = useState<WarrantyClaim[]>([])
  const [subs, setSubs] = useState<Sub[]>([])
  const [active, setActive] = useState<WarrantyClaim | null>(null)
  const [filter, setFilter] = useState<'open' | 'all' | 'closed'>('open')

  useEffect(() => {
    void api.warranty.list(project.id).then(setClaims)
    void api.subs.list().then(setSubs)
  }, [project.id])

  const filtered = useMemo(() => {
    if (filter === 'open') return claims.filter((c) => c.status !== 'closed' && c.status !== 'rejected')
    if (filter === 'closed') return claims.filter((c) => c.status === 'closed' || c.status === 'rejected')
    return claims
  }, [claims, filter])

  // SLA tracking: 24-hr ack, 48-hr dispatch (non-emergency) / same-day (emergency)
  function slaState(c: WarrantyClaim): { label: string; color: BadgeColor } | null {
    if (c.status === 'closed' || c.status === 'rejected' || c.status === 'resolved') return null
    const submitted = new Date(c.submitted_at)
    const hoursSinceSubmit = (Date.now() - submitted.getTime()) / 3_600_000
    if (!c.acknowledged_at && hoursSinceSubmit > 24) {
      return { label: `ACK OVERDUE ${Math.floor(hoursSinceSubmit)}h`, color: 'red' }
    }
    if (c.acknowledged_at && !c.dispatched_at) {
      const ackTime = new Date(c.acknowledged_at).getTime()
      const hoursSinceAck = (Date.now() - ackTime) / 3_600_000
      const limit = c.is_emergency ? 4 : 48
      if (hoursSinceAck > limit) {
        return { label: `DISPATCH OVERDUE ${Math.floor(hoursSinceAck)}h`, color: 'red' }
      }
    }
    return null
  }

  if (active) {
    return (
      <ClaimDetail
        claim={active}
        subs={subs}
        onClose={() => setActive(null)}
        onUpdated={(c) => {
          setClaims((p) => p.map((x) => (x.id === c.id ? c : x)))
          setActive(c)
        }}
        onDeleted={() => {
          setClaims((p) => p.filter((x) => x.id !== active.id))
          setActive(null)
        }}
      />
    )
  }

  const overdueCount = claims.filter((c) => slaState(c)?.color === 'red').length

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Warranty Claims</h2>
          <p className="muted small">
            {claims.length} total · {claims.filter((c) => c.status === 'submitted').length} unread
            {overdueCount > 0 && (
              <>
                {' '}· <span className="danger">{overdueCount} SLA overdue</span>
              </>
            )}
          </p>
        </div>
        <div className="row gap-2">
          <select className="search" value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
            <option value="open">Open</option>
            <option value="closed">Closed/Rejected</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div className="callout small mb-3">
        <strong>SLA targets:</strong> Acknowledge within 24 hrs of submission. Dispatch sub within
        48 hrs (non-emergency) or 4 hrs (emergency: water, heat, electrical). Statutory coverage:
        1yr workmanship, 2yr plumbing/electrical/HVAC, 10yr structural (MN 327A.02).
      </div>

      {filtered.length === 0 ? (
        <p className="muted">No claims in this view.</p>
      ) : (
        filtered.map((c) => {
          const sla = slaState(c)
          return (
            <div key={c.id} className="card mb-2" onClick={() => setActive(c)} style={{ cursor: 'pointer' }}>
              <div className="row-between">
                <div className="grow">
                  <div className="row-title">
                    {c.is_emergency === 1 && <span className="danger">⚠ EMERGENCY · </span>}
                    {c.title}
                  </div>
                  <div className="muted small">
                    {c.submitted_by_name ?? c.submitted_by} · {fmtDate(c.submitted_at)}
                    {c.location && ` · ${c.location}`}
                    {c.responsible_sub_name && ` · sub: ${c.responsible_sub_name}`}
                  </div>
                  <div className="muted small mt-1" style={{ maxWidth: 700 }}>
                    {c.description.slice(0, 200)}
                    {c.description.length > 200 && '…'}
                  </div>
                </div>
                <div className="text-right">
                  <Badge color={STATUS_COLOR[c.status]}>{c.status.replace('_', ' ').toUpperCase()}</Badge>
                  {sla && (
                    <div className="mt-1">
                      <Badge color={sla.color}>{sla.label}</Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })
      )}
    </section>
  )
}

// ----- Claim detail / triage view -------------------------------------------

function ClaimDetail({
  claim,
  subs,
  onClose,
  onUpdated,
  onDeleted,
}: {
  claim: WarrantyClaim
  subs: Sub[]
  onClose: () => void
  onUpdated: (c: WarrantyClaim) => void
  onDeleted: () => void
}) {
  const [draft, setDraft] = useState<Partial<WarrantyClaim>>(() => ({
    category: claim.category,
    responsible_sub_id: claim.responsible_sub_id,
    triage_notes: claim.triage_notes,
    resolution_notes: claim.resolution_notes,
  }))
  const [saving, setSaving] = useState(false)

  const dirty =
    (draft.category ?? null) !== claim.category ||
    (draft.responsible_sub_id ?? null) !== claim.responsible_sub_id ||
    (draft.triage_notes ?? null) !== claim.triage_notes ||
    (draft.resolution_notes ?? null) !== claim.resolution_notes

  const photos: string[] = (() => {
    try {
      return claim.photo_urls ? (JSON.parse(claim.photo_urls) as string[]) : []
    } catch {
      return []
    }
  })()

  async function saveDraft() {
    setSaving(true)
    try {
      const updated = await api.warranty.update(claim.id, {
        category: draft.category || null,
        responsible_sub_id: draft.responsible_sub_id || null,
        triage_notes: draft.triage_notes || null,
        resolution_notes: draft.resolution_notes || null,
      })
      onUpdated(updated)
    } finally {
      setSaving(false)
    }
  }

  async function setStatus(status: WarrantyClaim['status']) {
    if (
      !window.confirm(
        `Mark this claim as ${status.replace('_', ' ')}?\n\n` +
          `Audit log captures the change.`
      )
    )
      return
    const updated = await api.warranty.update(claim.id, { status })
    onUpdated(updated)
  }

  async function remove() {
    if (
      !window.confirm(
        'Delete this warranty claim? Permanent. Use Reject if you want to keep the record.'
      )
    )
      return
    await api.warranty.delete(claim.id)
    onDeleted()
  }

  // SLA timing display
  const submittedAgo = daysBetween(claim.submitted_at, new Date())
  const ackAgo = claim.acknowledged_at ? daysBetween(claim.acknowledged_at, new Date()) : null

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>
            {claim.is_emergency === 1 && <span className="danger">⚠ EMERGENCY · </span>}
            {claim.title}
          </h2>
          <p className="muted small">
            Submitted by {claim.submitted_by_name ?? claim.submitted_by} on{' '}
            {fmtDate(claim.submitted_at)} ({submittedAgo}d ago)
          </p>
        </div>
        <button className="btn-ghost" onClick={onClose}>
          ← Back
        </button>
      </div>

      <div className="card mb-3">
        <h3>Description</h3>
        <p style={{ whiteSpace: 'pre-wrap' }}>{claim.description}</p>
        {claim.location && <p className="muted small mt-2">Location: {claim.location}</p>}
      </div>

      {photos.length > 0 && (
        <div className="card mb-3">
          <h3>Photos ({photos.length})</h3>
          <div className="photo-grid">
            {photos.map((k) => (
              <a key={k} href={api.fileUrl(k)} target="_blank" rel="noopener noreferrer">
                <img className="photo-thumb" src={api.fileUrl(k)} alt="claim photo" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="card mb-3">
        <h3>Triage</h3>
        <label className="form-row">
          <span>Category</span>
          <select
            value={draft.category ?? ''}
            onChange={(e) => setDraft({ ...draft, category: (e.target.value || null) as WarrantyClaim['category'] })}
          >
            <option value="">— pick —</option>
            {CATEGORIES.map((c) => (
              <option key={c ?? ''} value={c ?? ''}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="form-row">
          <span>Responsible sub</span>
          <select
            value={draft.responsible_sub_id ?? ''}
            onChange={(e) => setDraft({ ...draft, responsible_sub_id: e.target.value || null })}
          >
            <option value="">— none —</option>
            {subs.map((s) => (
              <option key={s.id} value={s.id}>
                {s.company_name}
              </option>
            ))}
          </select>
        </label>
        <label className="form-row">
          <span>Triage notes</span>
          <textarea
            rows={3}
            value={(draft.triage_notes as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, triage_notes: e.target.value })}
          />
        </label>
        <label className="form-row">
          <span>Resolution notes</span>
          <textarea
            rows={3}
            value={(draft.resolution_notes as string) ?? ''}
            onChange={(e) => setDraft({ ...draft, resolution_notes: e.target.value })}
          />
        </label>

        <div className="row gap-2 mt-2">
          <button className="btn-primary small" onClick={saveDraft} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save triage' : 'No changes'}
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <h3>Status workflow</h3>
        <div className="muted small mb-2">
          Current: <Badge color={STATUS_COLOR[claim.status]}>{claim.status.replace('_', ' ').toUpperCase()}</Badge>
          {claim.acknowledged_at && (
            <>
              {' '}· acked {fmtDate(claim.acknowledged_at)} ({ackAgo}d ago)
            </>
          )}
          {claim.dispatched_at && <> · dispatched {fmtDate(claim.dispatched_at)}</>}
          {claim.resolved_at && <> · resolved {fmtDate(claim.resolved_at)}</>}
          {claim.closed_at && <> · closed {fmtDate(claim.closed_at)}</>}
        </div>
        <div className="row gap-2">
          {claim.status === 'submitted' && (
            <>
              <button className="btn-primary small" onClick={() => setStatus('acknowledged')}>
                Acknowledge
              </button>
              <button className="btn-ghost small" onClick={() => setStatus('rejected')}>
                Reject
              </button>
            </>
          )}
          {claim.status === 'acknowledged' && (
            <button className="btn-primary small" onClick={() => setStatus('dispatched')}>
              Mark sub dispatched
            </button>
          )}
          {claim.status === 'dispatched' && (
            <button className="btn-primary small" onClick={() => setStatus('in_progress')}>
              Mark in progress
            </button>
          )}
          {(claim.status === 'in_progress' || claim.status === 'dispatched') && (
            <button className="btn-primary small" onClick={() => setStatus('resolved')}>
              Mark resolved
            </button>
          )}
          {claim.status === 'resolved' && (
            <button className="btn-primary small" onClick={() => setStatus('closed')}>
              Close
            </button>
          )}
        </div>
      </div>

      <div className="row gap-2 mt-3">
        <button className="btn-ghost small" onClick={remove}>
          ✕ Delete claim
        </button>
      </div>
    </section>
  )
}
